import { useState } from "react";
import type { BusinessCalendar } from "@helpdesk/sla";

import { AssigneeSheet } from "../AssigneeSheet";
import { cn } from "../ui/cn";
import { ListEnd } from "./ListEnd";
import { TicketCard } from "./TicketCard";
import { TicketRow } from "./TicketRow";
import type { SortDirection, TicketSort } from "../../lib/api";
import type { Pipeline, TicketSummary, User } from "../../types";

interface TicketListPaneProps {
  tickets: TicketSummary[];
  pipelines: Pipeline[];
  users: User[];
  calendar: BusinessCalendar;
  selectedId: number | null;
  /** Ticket ids with customer mail the signed-in agent has not seen. */
  unread: Set<number>;
  sort: TicketSort;
  dir: SortDirection;
  loading: boolean;
  /** Another page exists behind the rows currently held. */
  hasMore: boolean;
  /** That next page is in flight. */
  loadingMore: boolean;
  onLoadMore: () => void;
  onSortChange: (sort: TicketSort) => void;
  onSelect: (id: number) => void;
  onAssigneeChange: (ticketId: number, assigneeId: number | null) => void;
  onPipelineChange: (ticketId: number, pipelineId: number | null) => void;

  /** Managers only: tick-boxes for acting on several tickets at once. */
  selectable?: boolean;
  checked?: Set<number>;
  onToggle?: (id: number) => void;
  /** Ticks or clears every ticket currently listed. */
  onToggleAll?: (ids: number[]) => void;

  /**
   * The signed-in agent, who leads the phone layout's assignee picker. Without
   * it the cards keep no separate assign button and behave as they always did.
   */
  currentUserId?: number;
}

/**
 * Column template, shared with TicketRow so the header lines up with the body.
 * Exported rather than duplicated because two grids that drift by one column
 * look like a rendering bug and are maddening to spot.
 *
 * Two literals rather than one built from a variable: Tailwind generates
 * arbitrary values by reading the source, so a template string assembled at
 * runtime produces a class that was never compiled and silently does nothing.
 * They differ only by the leading tick-box column -- keep them that way.
 */
export const LIST_GRID =
  "grid grid-cols-[7.5rem_0.875rem_minmax(10rem,1.3fr)_minmax(12rem,2fr)_4.5rem_4.5rem_6rem_9rem] items-center gap-3 px-3";

const LIST_GRID_SELECTABLE =
  "grid grid-cols-[1.5rem_7.5rem_0.875rem_minmax(10rem,1.3fr)_minmax(12rem,2fr)_4.5rem_4.5rem_6rem_9rem] items-center gap-3 px-3";

export function listGrid(selectable: boolean): string {
  return selectable ? LIST_GRID_SELECTABLE : LIST_GRID;
}

const COLUMNS: {
  sort: TicketSort;
  label: string;
  align?: "right";
  /**
   * The column is one glyph wide, so its heading is read rather than seen:
   * the word goes to screen readers and to the button's tooltip, and the
   * space goes to the columns that carry text. A truncated "C..." over a
   * column of icons would spend the width and say nothing.
   */
  glyph?: boolean;
}[] = [
  { sort: "last_message", label: "Last email" },
  { sort: "channel", label: "Channel", glyph: true },
  { sort: "from", label: "From" },
  { sort: "subject", label: "Subject" },
  { sort: "id", label: "Ticket" },
  { sort: "assignee", label: "Assigned" },
  { sort: "sla", label: "SLA" },
  { sort: "pipeline", label: "Pipeline" },
];

export function TicketListPane({
  tickets,
  pipelines,
  users,
  calendar,
  selectedId,
  unread,
  sort,
  dir,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  onSortChange,
  onSelect,
  onAssigneeChange,
  onPipelineChange,
  selectable = false,
  checked,
  onToggle,
  onToggleAll,
  currentUserId,
}: TicketListPaneProps) {
  /*
   * The ticket whose assignee picker is open, on a phone.
   *
   * Held here rather than per card so there is only ever one sheet, and so a
   * refetch that reorders the cards underneath it cannot leave it attached to
   * whichever ticket slid into that slot.
   */
  const [assigning, setAssigning] = useState<TicketSummary | null>(null);

  const ids = tickets.map((ticket) => ticket.id);
  const allChecked = ids.length > 0 && ids.every((id) => checked?.has(id));

  return (
    /*
     * One scroll container for both axes, with the header sticky inside it.
     * The header must scroll horizontally with the body, which a separately
     * positioned one would not.
     *
     * The minimum width is the point below which the columns stop being
     * readable rather than a target: the table fits a laptop without scrolling,
     * and only a genuinely narrow window pushes it into one.
     */
    <div className="h-full min-w-0 overflow-auto bg-surface">
      {/*
        Phone layout: cards, plus a sort control to stand in for the column
        headers -- sorting is not a feature the narrow layout should lose just
        because there are no headings left to click.
      */}
      <div className="md:hidden">
        {/*
          A line of text rather than a full-width select. The select is still
          there and still opens the platform's picker, laid invisibly over the
          words: drawn, it made a band as heavy as the filters above it, for a
          setting people change rarely.
        */}
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-surface-2 px-3 py-1">
          <label className="relative flex min-w-0 items-center gap-1 rounded-md py-1 text-xs text-muted focus-within:ring-2 focus-within:ring-brand-500/40">
            <span>Sorted by</span>
            <span className="truncate font-medium text-content">
              {COLUMNS.find((column) => column.sort === sort)?.label}
            </span>
            <span aria-hidden className="text-[10px]">
              {"\u25be"}
            </span>
            <select
              id="mobile-sort"
              value={sort}
              onChange={(event) => onSortChange(event.target.value as TicketSort)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            >
              {COLUMNS.map((column) => (
                <option key={column.sort} value={column.sort}>
                  {column.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => onSortChange(sort)}
            aria-label={`Sort ${dir === "asc" ? "descending" : "ascending"}`}
            title={`Sort ${dir === "asc" ? "descending" : "ascending"}`}
            className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-md text-xs text-muted hover:bg-surface"
          >
            {dir === "asc" ? "\u25b2" : "\u25bc"}
          </button>
        </div>

        {/* The cards get a wrapper of their own so the banding counts from the
            first card. Sharing a parent with the sort bar made it child two,
            which started the stripe on the opposite shade from the desktop
            table for no reason a reader could see. */}
        <div>
          {tickets.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted">
              {loading ? "Loading\u2026" : "No tickets match this view."}
            </p>
          ) : (
            <>
              {tickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  pipelines={pipelines}
                  users={users}
                  calendar={calendar}
                  selected={ticket.id === selectedId}
                  unread={unread.has(ticket.id)}
                  onSelect={onSelect}
                  onAssign={
                    currentUserId === undefined ? undefined : setAssigning
                  }
                />
              ))}
              <ListEnd
                hasMore={hasMore}
                loading={loadingMore}
                onReach={onLoadMore}
              />
            </>
          )}
        </div>
      </div>

      {assigning && currentUserId !== undefined ? (
        <AssigneeSheet
          users={users}
          currentUserId={currentUserId}
          assigneeId={assigning.assigneeId}
          subject={assigning.subject}
          onPick={(assigneeId) => onAssigneeChange(assigning.id, assigneeId)}
          onClose={() => setAssigning(null)}
        />
      ) : null}

      <div className="hidden min-w-[58rem] md:block">
        {/* Column headers double as the sort control. */}
        <div
          className={cn(
            listGrid(selectable),
            "sticky top-0 z-10 border-b border-line bg-surface-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-on-tint",
          )}
          role="row"
        >
          {selectable ? (
            <input
              type="checkbox"
              checked={allChecked}
              onChange={() => onToggleAll?.(ids)}
              aria-label={
                allChecked ? "Clear selection" : "Select every ticket listed"
              }
              title={allChecked ? "Clear selection" : "Select all"}
              className="size-3.5 accent-brand-600"
            />
          ) : null}

          {COLUMNS.map((column) => {
          const isActive = sort === column.sort;
          return (
            <button
              key={column.sort}
              type="button"
              onClick={() => onSortChange(column.sort)}
              aria-sort={
                isActive
                  ? dir === "asc"
                    ? "ascending"
                    : "descending"
                  : "none"
              }
              title={`Sort by ${column.label.toLowerCase()}`}
              className={cn(
                "flex min-w-0 items-center gap-1 rounded px-1 -mx-1 py-0.5 text-left uppercase",
                "hover:text-brand-600 dark:hover:text-brand-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                isActive && "text-content",
                column.align === "right" && "justify-end",
              )}
            >
              <span className={cn("truncate", column.glyph && "sr-only")}>
                {column.label}
              </span>
              {/*
                The arrow only renders on the active column. Showing a neutral
                glyph on every header reads as eight sorted columns at a glance.
              */}
              <span aria-hidden className="shrink-0 text-[9px] leading-none">
                {isActive ? (dir === "asc" ? "▲" : "▼") : ""}
              </span>
            </button>
          );
        })}
      </div>

        <div role="rowgroup">
          {tickets.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted">
              {loading ? "Loading…" : "No tickets match this view."}
            </p>
          ) : (
            <>
              {tickets.map((ticket) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  pipelines={pipelines}
                  users={users}
                  calendar={calendar}
                  selected={ticket.id === selectedId}
                  unread={unread.has(ticket.id)}
                  onSelect={onSelect}
                  onAssigneeChange={onAssigneeChange}
                  onPipelineChange={onPipelineChange}
                  selectable={selectable}
                  checked={checked?.has(ticket.id) ?? false}
                  onToggle={onToggle}
                />
              ))}
              <ListEnd
                hasMore={hasMore}
                loading={loadingMore}
                onReach={onLoadMore}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
